/**
 * Call Recording Hooks
 *
 * State-based hooks for call recording API operations.
 * Handles upload, processing status polling, notes, and transcript fetching.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { apiGet, apiPost, apiPut, apiUpload } from '@/services/api'

// ============================================================================
// Types
// ============================================================================

interface UploadRecordingRequest {
  audioData: string
  audioFormat: string
  durationSeconds: number
  ticketId?: number
}

interface UploadRecordingMultipartRequest {
  audioBlob: Blob
  whisperBlob?: Blob
  audioFormat: string
  durationSeconds: number
  ticketId?: number
}

interface AddToTicketRequest {
  ticketId: number
  noteText?: string
  noteType?: 'internal' | 'public' | 'resolution'
  includeTranscript?: boolean
  ticketUpdates?: {
    summary?: string
    priority_id?: number
    status_id?: number
    tickettype_id?: number
    category_1?: string
    category_2?: string
    impactlevel?: number
    urgencylevel?: number
  }
  timeEntry?: {
    hours: number
    notes?: string
  }
}

interface RecordingStatusData {
  status: string
  progress?: number
  errorMessage?: string
}

interface CallNotesData {
  id: string
  summary: string
  issueDescription: string
  discussionPoints: string[]
  resolution: string
  nextSteps: string[]
  customerSentiment: string
  formattedNote: string
  wasEdited: boolean
  addedToTicket: boolean
  originalNote?: string
}

interface TranscriptData {
  text: string
  segments: Array<{ start: number; end: number; text: string }>
  language: string
  wordCount: number
  audioDuration: number
}

interface TicketSuggestionsData {
  suggestedSubject: string
  suggestedPriorityScore: number
  suggestedPriorityId: number | null
  suggestedPriorityName: string | null
  priorityReason: string
  suggestedTicketTypeId: number | null
  suggestedTicketTypeName: string | null
  suggestedCategoryId: number | null
  suggestedCategoryName: string | null
  suggestedSubcategory: string | null
  suggestedStatusId: number | null
  suggestedStatusName: string | null
  suggestedImpactLevel: number
  suggestedUrgencyLevel: number
  suggestedTimeHours: number
  callDurationMinutes: number
  availableStatuses: Array<{ id: number; name: string }>
  availablePriorities: Array<{ id: number; name: string }>
  availableTicketTypes: Array<{ id: number; name: string }>
  availableCategories: Array<{ id: number; name: string }>
  availableSubcategories?: Array<{ id: number; name: string; parentId: number }>
  currentTicket?: {
    statusId?: number
    statusName?: string
    priorityId?: number
    priorityName?: string
    impactLevel?: number
    urgencyLevel?: number
    ticketTypeId?: number
    ticketTypeName?: string
  } | null
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Poll for recording status during processing
 */
export function useRecordingStatus(
  recordingId: string | null,
  options?: { pollingInterval?: number; timeoutMs?: number }
) {
  const [data, setData] = useState<RecordingStatusData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [timedOut, setTimedOut] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!recordingId) {
      setData(null)
      setTimedOut(false)
      return
    }

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }

    const fetchStatus = async () => {
      try {
        setIsLoading(true)
        const result = await apiGet<RecordingStatusData>(`/api/calls/${recordingId}/status`)
        setData(result)
        setError(null)

        // Stop polling if completed or failed
        if (result.status === 'completed' || result.status === 'failed') {
          stopPolling()
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)))
      } finally {
        setIsLoading(false)
      }
    }

    fetchStatus()
    intervalRef.current = setInterval(fetchStatus, options?.pollingInterval || 2000)

    // 10-minute polling timeout (prevents polling forever if stuck)
    const timeout = options?.timeoutMs ?? 10 * 60 * 1000
    timeoutRef.current = setTimeout(() => {
      stopPolling()
      setTimedOut(true)
      setData(prev => prev ? { ...prev, status: 'failed', errorMessage: 'Processing timed out after 10 minutes' } : { status: 'failed', errorMessage: 'Processing timed out after 10 minutes' })
    }, timeout)

    return () => stopPolling()
  }, [recordingId, options?.pollingInterval, options?.timeoutMs])

  return { data, isLoading, error, timedOut }
}

/**
 * Fetch call notes for a recording
 */
export function useCallNotes(recordingId: string | null) {
  const [data, setData] = useState<CallNotesData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!recordingId) {
      setData(null)
      return
    }

    const fetchNotes = async () => {
      try {
        setIsLoading(true)
        const result = await apiGet<CallNotesData>(`/api/calls/${recordingId}/notes`)
        setData(result)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)))
      } finally {
        setIsLoading(false)
      }
    }

    fetchNotes()
  }, [recordingId])

  return { data, isLoading, error }
}

/**
 * Fetch transcript for a recording
 */
export function useTranscript(recordingId: string | null) {
  const [data, setData] = useState<TranscriptData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!recordingId) {
      setData(null)
      return
    }

    const fetchTranscript = async () => {
      try {
        setIsLoading(true)
        const result = await apiGet<TranscriptData>(`/api/calls/${recordingId}/transcript`)
        setData(result)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)))
      } finally {
        setIsLoading(false)
      }
    }

    fetchTranscript()
  }, [recordingId])

  return { data, isLoading, error }
}

/**
 * Fetch AI-suggested ticket attributes for a recording
 */
export function useTicketAttributeSuggestions(
  recordingId: string | null,
  ticketId?: number | null
) {
  const [data, setData] = useState<TicketSuggestionsData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!recordingId) {
      setData(null)
      return
    }

    const fetchSuggestions = async () => {
      try {
        setIsLoading(true)
        const params: Record<string, string> = {}
        if (ticketId) params.ticketId = String(ticketId)
        const queryString = new URLSearchParams(params).toString()
        const url = `/api/calls/${recordingId}/suggest-ticket-attributes${queryString ? `?${queryString}` : ''}`
        const result = await apiGet<TicketSuggestionsData>(url)
        setData(result)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)))
      } finally {
        setIsLoading(false)
      }
    }

    fetchSuggestions()
  }, [recordingId, ticketId])

  return { data, isLoading, error }
}

// ============================================================================
// Mutation helpers (imperative, not hooks)
// ============================================================================

/**
 * Update call notes
 */
export async function updateCallNotes(recordingId: string, editedNote: string): Promise<void> {
  await apiPut(`/api/calls/${recordingId}/notes`, { editedNote })
}

/**
 * Retry processing a failed or stuck recording
 */
export async function retryProcessing(recordingId: string): Promise<void> {
  await apiPost('/api/calls/process', { recordingId, generateNotes: true })
}

/**
 * Add notes to a Halo PSA ticket
 */
export async function addNotesToTicket(
  recordingId: string,
  data: AddToTicketRequest
): Promise<unknown> {
  return apiPost(`/api/calls/${recordingId}/add-to-ticket`, data as unknown as Record<string, unknown>)
}

export type { TicketSuggestionsData, AddToTicketRequest }

/**
 * Direct-to-storage upload: bypasses Vercel's 4.5 MB body size limit by
 * uploading blobs directly to Supabase Storage using signed URLs.
 */
async function uploadDirectToStorage(data: UploadRecordingMultipartRequest): Promise<string> {
  // 1. Get signed upload URLs + create DB record
  const initResult = await apiPost<{
    recordingId: string
    psaUploadUrl: string
    psaUploadToken: string
    psaContentType: string
    whisperUploadUrl: string | null
    whisperUploadToken: string | null
  }>('/api/calls/upload-direct', {
    audioFormat: data.audioFormat,
    durationSeconds: data.durationSeconds,
    fileSizeBytes: data.audioBlob.size,
    ticketId: data.ticketId,
    hasWhisperStream: !!data.whisperBlob,
  } as unknown as Record<string, unknown>)

  const { recordingId } = initResult

  // 2. Upload PSA blob directly to Supabase Storage
  console.log(`[RecordingFlow] Direct upload PSA: ${(data.audioBlob.size / 1024 / 1024).toFixed(1)} MB`)
  const psaRes = await fetch(initResult.psaUploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': initResult.psaContentType },
    body: data.audioBlob,
  })

  if (!psaRes.ok) {
    const errText = await psaRes.text().catch(() => '')
    throw new Error(`PSA upload failed (${psaRes.status}): ${errText}`)
  }

  // 3. Upload whisper blob if available
  if (data.whisperBlob && initResult.whisperUploadUrl) {
    console.log(`[RecordingFlow] Direct upload Whisper: ${(data.whisperBlob.size / 1024 / 1024).toFixed(1)} MB`)
    const whisperRes = await fetch(initResult.whisperUploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'audio/webm' },
      body: data.whisperBlob,
    })

    if (!whisperRes.ok) {
      console.warn(`[RecordingFlow] Whisper direct upload failed (${whisperRes.status}) — will use PSA for transcription`)
    }
  }

  // 4. Mark upload as complete
  await apiPost('/api/calls/upload-complete', {
    recordingId,
    fileSizeBytes: data.audioBlob.size,
  })

  console.log(`[RecordingFlow] Direct upload complete: ${recordingId}`)
  return recordingId
}

/**
 * Combined upload + process flow
 */
export function useRecordingFlow() {
  const [isUploading, setIsUploading] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Legacy base64 upload
  const uploadAndProcess = useCallback(async (
    data: UploadRecordingRequest
  ): Promise<string> => {
    try {
      setError(null)
      setIsUploading(true)

      const uploadResult = await apiPost<{ recordingId: string }>('/api/calls/upload', data as unknown as Record<string, unknown>)
      const recordingId = uploadResult.recordingId

      setIsUploading(false)
      setIsProcessing(true)

      // Fire-and-forget: don't block on processing
      apiPost('/api/calls/process', {
        recordingId,
        generateNotes: true,
      }).catch(err => {
        console.error('[RecordingFlow] Process call failed:', err)
      })

      return recordingId
    } catch (err) {
      setIsUploading(false)
      setIsProcessing(false)
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    }
  }, [])

  // WS4: Multipart binary upload — no base64 overhead
  // Falls back to direct-to-storage upload if multipart fails with 413
  const uploadAndProcessMultipart = useCallback(async (
    data: UploadRecordingMultipartRequest
  ): Promise<string> => {
    try {
      setError(null)
      setIsUploading(true)

      let recordingId: string

      // Vercel serverless functions have a 4.5 MB body size limit.
      // Use direct-to-storage upload for files over 4 MB to avoid 413 errors entirely.
      const totalSize = data.audioBlob.size + (data.whisperBlob?.size || 0)
      const DIRECT_UPLOAD_THRESHOLD = 4 * 1024 * 1024 // 4 MB

      if (totalSize > DIRECT_UPLOAD_THRESHOLD) {
        console.log(`[RecordingFlow] File size ${(totalSize / 1024 / 1024).toFixed(1)} MB exceeds threshold — using direct-to-storage upload`)
        recordingId = await uploadDirectToStorage(data)
      } else {
        const formData = new FormData()
        formData.append('audio_psa', data.audioBlob, `recording.${data.audioFormat}`)
        if (data.whisperBlob) {
          formData.append('audio_whisper', data.whisperBlob, 'recording_whisper.webm')
        }
        formData.append('audioFormat', data.audioFormat)
        formData.append('durationSeconds', String(data.durationSeconds))
        if (data.ticketId) formData.append('ticketId', String(data.ticketId))

        try {
          const uploadResult = await apiUpload<{ recordingId: string }>('/api/calls/upload', formData)
          recordingId = uploadResult.recordingId
        } catch (uploadErr: any) {
          const msg = uploadErr.message?.toLowerCase() || ''
          const isUploadSizeError = msg.includes('413')
            || msg.includes('too large')
            || msg.includes('payload')
            || msg.includes('failed to fetch')
            || msg.includes('network')
            || msg.includes('aborted')
            || uploadErr instanceof TypeError
          if (isUploadSizeError) {
            console.log(`[RecordingFlow] Multipart upload failed (${uploadErr.message}) — falling back to direct-to-storage upload`)
            recordingId = await uploadDirectToStorage(data)
          } else {
            throw uploadErr
          }
        }
      }

      setIsUploading(false)
      setIsProcessing(true)

      // Fire-and-forget: don't block on processing
      apiPost('/api/calls/process', {
        recordingId,
        generateNotes: true,
      }).catch(err => {
        console.error('[RecordingFlow] Process call failed:', err)
      })

      return recordingId
    } catch (err) {
      setIsUploading(false)
      setIsProcessing(false)
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    }
  }, [])

  return {
    uploadAndProcess,
    uploadAndProcessMultipart,
    isUploading,
    isProcessing,
    isLoading: isUploading || isProcessing,
    error,
  }
}

// ============================================================================
// Mid-call update types
// ============================================================================

export type MidCallUpdatePhase =
  | 'idle'
  | 'extracting'
  | 'uploading'
  | 'processing'
  | 'suggesting'
  | 'updating'
  | 'complete'
  | 'error'

interface MidCallUpdateLog {
  timestamp: Date
  recordingId: string
  phase: MidCallUpdatePhase
  message: string
}

/**
 * Mid-call update: upload partial audio, process, suggest attributes, update ticket.
 * Returns an imperative trigger function + status for UI display.
 */
export function useMidCallUpdate(ticketId?: number) {
  const [phase, setPhase] = useState<MidCallUpdatePhase>('idle')
  const [message, setMessage] = useState<string | null>(null)
  const [updateCount, setUpdateCount] = useState(0)
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null)
  const [updateLog, setUpdateLog] = useState<MidCallUpdateLog[]>([])
  const abortRef = useRef(false)

  const addLog = useCallback((recordingId: string, logPhase: MidCallUpdatePhase, msg: string) => {
    setUpdateLog(prev => [...prev, { timestamp: new Date(), recordingId, phase: logPhase, message: msg }])
  }, [])

  const pollForCompletion = useCallback(async (recordingId: string): Promise<boolean> => {
    const maxAttempts = 120 // 4 minutes max at 2s intervals
    for (let i = 0; i < maxAttempts; i++) {
      if (abortRef.current) return false
      await new Promise(resolve => setTimeout(resolve, 2000))
      try {
        const result = await apiGet<RecordingStatusData>(`/api/calls/${recordingId}/status`)
        if (result.status === 'completed') return true
        if (result.status === 'failed') {
          setMessage(result.errorMessage || 'Processing failed')
          return false
        }
      } catch {
        // Retry on network errors
      }
    }
    return false
  }, [])

  const triggerUpdate = useCallback(async (
    audioBlob: Blob,
    durationSeconds: number,
  ): Promise<boolean> => {
    if (!ticketId) {
      setPhase('error')
      setMessage('No ticket ID for mid-call update')
      return false
    }

    abortRef.current = false

    try {
      // 1. Upload
      setPhase('uploading')
      setMessage('Uploading audio snapshot...')

      const { blobToBase64, getAudioFormat } = await import('@/hooks/useCallRecording')
      const base64 = await blobToBase64(audioBlob)
      const format = getAudioFormat(audioBlob.type)

      const uploadResult = await apiPost<{ recordingId: string }>('/api/calls/upload', {
        audioData: base64,
        audioFormat: format,
        durationSeconds,
        ticketId,
      } as unknown as Record<string, unknown>)
      const recordingId = uploadResult.recordingId
      addLog(recordingId, 'uploading', 'Audio uploaded')

      if (abortRef.current) return false

      // 2. Process (transcribe + generate notes)
      setPhase('processing')
      setMessage('Transcribing & generating notes...')

      await apiPost('/api/calls/process', { recordingId, generateNotes: true })
      addLog(recordingId, 'processing', 'Processing started')

      // 3. Poll for completion
      const completed = await pollForCompletion(recordingId)
      if (!completed || abortRef.current) {
        if (!abortRef.current) {
          setPhase('error')
          setMessage('Processing did not complete')
        }
        return false
      }
      addLog(recordingId, 'processing', 'Processing complete')

      // 4. Fetch AI suggestions
      setPhase('suggesting')
      setMessage('Getting AI suggestions...')

      let suggestions: TicketSuggestionsData | null = null
      try {
        const params = new URLSearchParams({ ticketId: String(ticketId) })
        suggestions = await apiGet<TicketSuggestionsData>(
          `/api/calls/${recordingId}/suggest-ticket-attributes?${params}`
        )
        addLog(recordingId, 'suggesting', 'Suggestions received')
      } catch {
        addLog(recordingId, 'suggesting', 'Suggestions failed, applying notes only')
      }

      if (abortRef.current) return false

      // 5. Add notes + suggestions to ticket
      setPhase('updating')
      setMessage('Updating ticket...')

      const ticketUpdates: AddToTicketRequest['ticketUpdates'] = {}
      if (suggestions) {
        if (suggestions.suggestedSubject) ticketUpdates.summary = suggestions.suggestedSubject
        if (suggestions.suggestedPriorityId) ticketUpdates.priority_id = suggestions.suggestedPriorityId
        if (suggestions.suggestedTicketTypeId) ticketUpdates.tickettype_id = suggestions.suggestedTicketTypeId
        if (suggestions.suggestedCategoryName) ticketUpdates.category_1 = suggestions.suggestedCategoryName
        if (suggestions.suggestedSubcategory) ticketUpdates.category_2 = suggestions.suggestedSubcategory
        if (suggestions.suggestedStatusId) ticketUpdates.status_id = suggestions.suggestedStatusId
        if (suggestions.suggestedImpactLevel) ticketUpdates.impactlevel = suggestions.suggestedImpactLevel
        if (suggestions.suggestedUrgencyLevel) ticketUpdates.urgencylevel = suggestions.suggestedUrgencyLevel
      }

      await addNotesToTicket(recordingId, {
        ticketId,
        noteType: 'internal',
        ticketUpdates: Object.keys(ticketUpdates).length > 0 ? ticketUpdates : undefined,
      })
      addLog(recordingId, 'updating', 'Ticket updated')

      // 6. Done
      setPhase('complete')
      setMessage(`Ticket updated at ${new Date().toLocaleTimeString()}`)
      setUpdateCount(prev => prev + 1)
      setLastUpdateTime(new Date())

      // Reset to idle after 5 seconds
      setTimeout(() => {
        setPhase('idle')
        setMessage(null)
      }, 5000)

      return true
    } catch (err) {
      setPhase('error')
      setMessage(err instanceof Error ? err.message : 'Mid-call update failed')
      // Reset to idle after 8 seconds on error
      setTimeout(() => {
        setPhase('idle')
        setMessage(null)
      }, 8000)
      return false
    }
  }, [ticketId, addLog, pollForCompletion])

  const abort = useCallback(() => {
    abortRef.current = true
    setPhase('idle')
    setMessage(null)
  }, [])

  return {
    triggerUpdate,
    abort,
    phase,
    message,
    updateCount,
    lastUpdateTime,
    updateLog,
    isActive: phase !== 'idle' && phase !== 'complete' && phase !== 'error',
  }
}
